<script setup lang="ts">
import type { ProductCardHorizontalProps } from '@design-system/components/UI/UIProductCardHorizontal.props'
import type { AlgoliaProductType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'

const props = defineProps<{
  tag: string
  brand: string
  gender?: string
  excludeObjectId: string
}>()

const { isDialogOpen } = useDialog(
  SIDEBAR_IDS.pdpEcommerceBlockProductTagSideSlide
)
const { getProductsByTag } = useProductsByTag()
const { skuPrices, fetchSkuPrices } = useProductTileClPrices()
const { openQuickBuySlide } = useUIQuickBuyPopUpSlide()

const products = ref<AlgoliaProductType[]>([])
const isLoading = ref(false)
const hasError = ref(false)

const openQuickBuyPopUpLayer = (product: ProductCardHorizontalProps) => {
  openQuickBuySlide({
    product,
    isQuickBuyPlp: true,
  })
}

const desktopAlignment = () => 0.6

const productsWithPrices = computed(() =>
  products.value.map(product => {
    const sku = product.size?.[0]?.SKU
    return {
      ...product,
      ...(sku ? { clPrices: skuPrices.value?.[sku] } : {}),
    }
  })
)

const fetchProducts = async (tag: string) => {
  if (!tag) {
    products.value = []
    return
  }

  isLoading.value = true
  hasError.value = false

  try {
    const hits = await getProductsByTag({
      tag,
      brand: props.brand,
      gender: props.gender,
      excludeObjectId: props.excludeObjectId,
      hitsPerPage: 12,
    })
    products.value = (hits ?? []).filter(isNonNullable) as AlgoliaProductType[]

    const skuCodes =
      products.value
        .map(item => item.size?.at(0)?.SKU)
        .filter((sku): sku is string => !!sku) ?? []

    if (skuCodes.length) {
      await fetchSkuPrices(skuCodes).catch(() => {
        // Keep products visible even if Commerce Layer prices fail.
      })
    }
  } catch (error) {
    console.error('[PdpProductTagSideSlide] fetch failed', { tag, error })
    products.value = []
    hasError.value = true
  } finally {
    isLoading.value = false
  }
}

watch(
  [isDialogOpen, () => props.tag],
  async ([isOpen, tag]) => {
    if (!isOpen || !tag) return
    await fetchProducts(tag)
  },
  { immediate: true }
)
</script>

<template>
  <OrganismsSidebarSlide
    :id="SIDEBAR_IDS.pdpEcommerceBlockProductTagSideSlide"
    :header-props="{
      titleText: tag,
      hasCloseButton: true,
    }"
    body-class="overflow-x-hidden"
  >
    <template #body>
      <div class="text-primitives-black pb-10 pt-2">
        <p class="text-light-6 text-background-primary mb-10">
          {{ $ts('pdp.productTagSideSlide.description') }}
        </p>
        <div
          v-if="isLoading"
          class="flex min-h-48 items-center justify-center py-10"
        >
          <DefaultIconsLoader class="h-8 w-8" />
        </div>

        <p
          v-else-if="hasError || !productsWithPrices.length"
          class="text-light-6 py-10"
        >
          {{ $ts('sharedProductsList.empty') }}
        </p>

        <!-- Algolia, not a recommendation partner, so `partner` stays empty -->
        <CarouselTrackingProvider v-else name="product_tag" partner="">
          <UISliderWithArrows
            :id="`pdp-tag-carousel-${tag}`"
            :items="productsWithPrices"
            :desktop-alignment="desktopAlignment"
            hide-lateral-slides-on-mobile
            has-slide-vertical-border
            slide-custom-width="w-8/12 lg:w-6/12"
            :loop="productsWithPrices.length > 2"
            :show-navigation="productsWithPrices.length > 2"
          >
            <template #default="item">
              <WrapperProductTile :product="item">
                <template #default="{ product, productCardHorizontal }">
                  <UIProductTile
                    disable-swipe-mobile
                    :product="{
                      ...product,
                      showLabels: true,
                    }"
                    @open-quick-buy="
                      () =>
                        productCardHorizontal &&
                        openQuickBuyPopUpLayer(productCardHorizontal)
                    "
                  />
                </template>
              </WrapperProductTile>
            </template>
          </UISliderWithArrows>
        </CarouselTrackingProvider>
      </div>
    </template>
  </OrganismsSidebarSlide>
</template>

<style scoped lang="scss">
:deep(.slider-base-container) {
  margin: 0;
}
:deep(.slider-base-container .embla) {
  overflow: visible;
}
:deep(
    .slider-base-container .navigation-buttons-container .navigation-button-prev
  ) {
  left: 0;
}
:deep(
    .slider-base-container .navigation-buttons-container .navigation-button-next
  ) {
  right: 0;
}
:deep(.navigation-button-prev .navigation-button),
:deep(.navigation-button-next .navigation-button) {
  transform: unset;
  width: var(--positive-padding-x);
}
:deep(.navigation-button-prev .navigation-button) {
  left: calc(var(--positive-padding-x) * -1);
}

:deep(.navigation-button-next .navigation-button) {
  right: calc(var(--positive-padding-x) * -1);
}
@media (min-width: 1024px) {
  :deep(.embla) {
    margin: 0;
  }
}
</style>
